import { z } from 'zod';

/**
 * The number printed on the receipt a customer puts on the counter, and the
 * shape a lookup will accept before it makes a request.
 *
 * `LK-000482` is the till's prefix, a hyphen, and the counter value padded to six
 * (FR-410). The API's `parseSaleNumber` reads it back and refuses anything else
 * with `VALIDATION_FAILED` — 400, non-retryable — before the value reaches
 * PostgreSQL. **Everything below is that same rule restated on this side of the
 * wire**, so somebody who has misread a faded thermal print gets a sentence under
 * the field instead of a round trip that comes back as a code.
 *
 * ## The prefix is optional here, and that is the whole difference from a ticket
 *
 * `tickets/ticket-number.ts` refuses a prefixless number, and it is right to:
 * a ticket *is* the admission, so the one it names has to be the one that was
 * scanned. A receipt only evidences a purchase that already happened, and the API
 * says so in as many words — `parseSaleNumber` admits the counter half alone
 * where `parseTicketNumber` refuses it, for a receipt whose top line is torn or
 * which is being read out over the phone.
 *
 * **So this file must not be written by copying the ticket's.** The two look
 * almost identical and differ on exactly the input that matters: `482`. Refusing
 * it here would refuse a lookup the server would have answered, and the person
 * refused is the one holding a damaged receipt — precisely the case the API went
 * out of its way to serve.
 *
 * What the prefixless form costs is stated where it is paid, in
 * `ReceiptLookup.tsx`: **the counter is the terminal's** (R-402), so
 * `sales_terminal_kind_number_key` is unique on `(terminal_id, sequence_kind,
 * number_value)` and on nothing narrower. Till LK and till GT each print a 482
 * for two different amounts. A number without its prefix therefore names as many
 * sales as there are tills in the caller's reach, and the answer is a collection
 * somebody chooses from rather than a first row this app picks for them.
 *
 * ## It restates the server's rule and does not add to it
 *
 * The temptation on a field like this is to insist on the *printed* form —
 * exactly six digits, a prefix that looks like two letters. Both would refuse
 * receipts the API accepts:
 *
 * - **Padding is presentation.** `formatTerminalNumber` pads to six with
 *   `padStart`, which widens rather than truncates, so a till past 999,999 prints
 *   seven digits. `LK-482`, `LK-000482` and `LK-0000000482` are one receipt, and
 *   a field insisting on six would stop reading its own printer's output at the
 *   one boundary where it matters.
 * - **A prefix is `terminals.prefix`, which is unique and otherwise
 *   unconstrained.** A park that names a till `GATE-N` is doing nothing the
 *   database forbids, which is why the separator below is the *last* hyphen and
 *   not the first.
 *
 * ## Case is the server's to fix, and it does
 *
 * Prefixes are stored uppercase — `TerminalsService.create` uppercases before the
 * row is written — and `parseTerminalNumber` uppercases what it is given. So
 * `lk-000482` finds the same sale as `LK-000482`, and this file deliberately does
 * **not** uppercase on the way out: the value that was typed is the value that is
 * sent, the answer comes back carrying the server's own canonical
 * `receiptNumber`, and there is no place for this app to have an opinion about
 * case that could ever disagree with the one that decides.
 */

/**
 * The separator, and the **last** one in the string rather than the first, for
 * the reason the head of this file gives — `GATE-N-000482` is prefix `GATE-N` and
 * value `000482`, because the padded value can never contain one.
 */
const SEPARATOR = '-';

/**
 * The value half, exactly: one or more ASCII digits and nothing else.
 *
 * Deliberately a pattern rather than a `try { BigInt(x) }`. `BigInt(' 482')` is
 * `482n`, `BigInt('0x1e2')` is `482n` and `BigInt('')` is `0n` — each accepted by
 * the bare conversion, and each a different number from the one on the paper. A
 * leading `+` or `-` is refused by the same anchor, which is what keeps `-000001`
 * a malformed shape rather than a negative counter value.
 */
const DIGITS = /^[0-9]+$/;

/**
 * What a counter can have printed: `1` to the largest value PostgreSQL's `bigint`
 * holds.
 *
 * Restated from the API's `MIN_COUNTER_VALUE` and `MAX_COUNTER_VALUE` because
 * there is nothing to import — the published document carries no bound for this
 * path parameter, only the prose that names it. Zero means nothing was printed,
 * and above `int8` the API refuses the conversion rather than letting the driver
 * raise something that leaves as a *retryable* internal error.
 *
 * `BigInt` and not `Number`: a counter value near the top of that range is past
 * `Number.MAX_SAFE_INTEGER`, so comparing as a number would accept values above
 * the bound and refuse values below it, both silently.
 */
const MIN_COUNTER_VALUE = 1n;
const MAX_COUNTER_VALUE = 9223372036854775807n;

/** The whole printed form, as a person reads it. Used in copy and as the field's example. */
export const RECEIPT_NUMBER_EXAMPLE = 'LK-000482';

/** The counter half on its own, for the copy that says this is accepted too. */
export const RECEIPT_COUNTER_EXAMPLE = '482';

export const RECEIPT_NUMBER_REQUIRED = 'Enter the number printed on the receipt.';

/**
 * One message for every malformed shape, matching the API's own single constraint
 * string.
 *
 * Not four messages for the four ways the shape can be wrong. The remedy is
 * identical in every case — read the number off the receipt again — and a refusal
 * that distinguished "well formed but too large" from "not a number at all" would
 * be a small oracle about what this park's counters have reached, which is the
 * reason the API words it once too.
 */
export const RECEIPT_NUMBER_MALFORMED = `That is not the shape of a receipt number. One reads ${RECEIPT_NUMBER_EXAMPLE} — the till's prefix, a hyphen, and the number printed on the receipt. The number on its own (${RECEIPT_COUNTER_EXAMPLE}) is accepted too.`;

/**
 * Whether a counter value is one a till could have printed.
 *
 * Assumes the digits have already been matched, which is what makes the `BigInt`
 * conversion safe.
 */
function isPrintableCounter(digits: string): boolean {
  const counterValue = BigInt(digits);

  return counterValue >= MIN_COUNTER_VALUE && counterValue <= MAX_COUNTER_VALUE;
}

/**
 * Whether a value is shaped like something a till has printed on a receipt.
 *
 * Answers about the *shape* only. Whether a sale carrying it exists, whether the
 * caller may see it, and how many tills have printed it are questions with one
 * answer each and the server is the only thing that can give them.
 *
 * Two accepted forms, which is one more than the ticket field has:
 *
 * - `LK-000482` — the whole display form. The prefix resolves the till, so at
 *   most one sale can come back.
 * - `482`, `000482` — the counter half alone, off a receipt whose top line is
 *   gone. Every till in the caller's reach that has printed a 482 answers.
 */
export function isPrintedReceiptNumber(value: string): boolean {
  const separator = value.lastIndexOf(SEPARATOR);

  // No separator at all. Unlike a ticket number this is not a refusal on its own
  // — it is the counter half, and the digits decide.
  if (separator === -1) return DIGITS.test(value) && isPrintableCounter(value);

  // `-000001` — a separator at 0, so the prefix is empty. `LK-` — a separator at
  // the end, so the value is. Two shapes, one refusal, because to the person who
  // typed it they are one fault.
  if (separator === 0 || separator === value.length - 1) return false;

  const printed = value.slice(separator + 1);

  return DIGITS.test(printed) && isPrintableCounter(printed);
}

/**
 * Whether what was typed names a single till.
 *
 * The presence of a prefix is exactly the difference between a lookup that can
 * answer with one sale and one that can answer with eleven — see the head of this
 * file. The lookup uses it to word the empty answer honestly: a bare counter that
 * found nothing was asked of every till in reach, and there is no prefix to
 * suggest re-reading.
 *
 * **Assumes {@link isPrintedReceiptNumber} has already accepted the value**, so
 * a separator here means a non-empty prefix.
 */
export const namesOneTill = (value: string): boolean => value.includes(SEPARATOR);

/**
 * A receipt number somebody is looking up.
 *
 * Trimmed before it is judged, so a value pasted with a trailing space is a space
 * and not a malformed number — and so the value the form submits is the value it
 * validated.
 */
export const receiptNumberSchema = z
  .string()
  .trim()
  .min(1, RECEIPT_NUMBER_REQUIRED)
  .refine(isPrintedReceiptNumber, RECEIPT_NUMBER_MALFORMED);
